import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { UserAuthRole } from 'lemunzo-api';
import { filter, map, Observable, of, switchMap, take } from 'rxjs';
import { CurrentUser } from '../models/current-user';
import { CurrentUserService } from '../services/current-user.service';

@Injectable({
    providedIn: 'root',
})
export class RoleGuard {
    constructor(
        private readonly currentUserService: CurrentUserService,
        private readonly msalBroadcastService: MsalBroadcastService,
        private readonly router: Router
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        _state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> {
        const routeRoles = route.data['roles'] as UserAuthRole[];

        // Wait for MSAL to settle (account + claims fully loaded) before reading the
        // current user - evaluating immediately can race CurrentUserService's own
        // inProgress$ subscription and read a stale/empty role.
        return this.msalBroadcastService.inProgress$.pipe(
            filter((status) => status === InteractionStatus.None),
            take(1),
            switchMap(() => this.evaluate(routeRoles, route))
        );
    }

    private evaluate(
        routeRoles: UserAuthRole[],
        route: ActivatedRouteSnapshot
    ): Observable<boolean | UrlTree> {
        const user = this.currentUserService.getCurrentUser();

        if (user == null) {
            console.debug(
                '[RoleGuard] Token missing or invalid. Redirecting to Auth page.'
            );
            return of(this.router.parseUrl('/auth'));
        }

        console.debug('[RoleGuard] Authorizing access to ' + route.url, user);

        if (this.isAuthorized(routeRoles, user)) {
            return of(true);
        }

        // The account is present but its cached role claim didn't match - it may just be
        // stale (e.g. the cached ID token was evicted by a dev-server hot-reload race, or
        // has expired) even though the account's SSO session is still valid. Try a silent
        // refresh before concluding the user is genuinely unauthorized.
        console.debug(
            '[RoleGuard] Role claim did not match required roles, attempting silent refresh before redirecting to Error page.'
        );
        return this.currentUserService.refreshCurrentUser().pipe(
            map((refreshedUser) => {
                if (
                    refreshedUser &&
                    this.isAuthorized(routeRoles, refreshedUser)
                ) {
                    return true;
                }

                console.debug(
                    '[RoleGuard] Different user role than required. Redirecting to Error page.'
                );
                return this.router.parseUrl('/error');
            })
        );
    }

    private isAuthorized(
        routeRoles: UserAuthRole[],
        user: CurrentUser
    ): boolean {
        if (routeRoles.length === 0) {
            return true;
        }

        return routeRoles.some((role) => role === user.role);
    }
}
